import { Grid } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import {
  Add,
  Button,
  CardComponent,
} from '@HomaHealth/ui-component-library/lib/_AIQ-components';
import { financialForensicsConstants } from '../../../core-utils/messages/financialForensicsConstants';
import React from 'react';
import {
  calculateContributions,
  calculateVariableContributions,
  ContributionEquation,
} from '../../../core-utils/helper-utils/financialForensicsHelper';
import {
  ClaimsModalType,
  OtherClaimsModalType,
  ReimbursementModalType,
  VendorModalType,
} from '../../../pages/FinancialForensicsPage/FinancialForensics.types';
import { CircularLoader } from '@HomaHealth/ui-component-library';
import { VendorEntry } from '../../../pages/FinancialForensicsPage/types/VariableCost.types';
import { Vendor } from '../../../pages/FinancialForensicsPage/types/FixedCost.types';
import { patientAccessTheme } from '@HomaHealth/ui-component-library/lib/theme/theme';
import { Column } from '../ContributionTable/TableRow';
import { ContributionTable } from '../ContributionTable/ContributionTable';
import { AddVendorDropdown } from '../ContributionTable/AddVendorDropdown';

type FinancialsType = {
  fixedCostColumns: Column[];
  variableCostColumns: Column[];
  fixedPrimary: Vendor[];
  fixedSecondary: Vendor[];
  fixedMember: Vendor[];
  fixedTertiary?: Vendor[];
  variablePrimary: VendorEntry[];
  variableSecondary: VendorEntry[];
  variableMember: VendorEntry[];
  fixedCostTableLoading: boolean;
  variableCostTableLoading: boolean;
  setOpenVendorModal?: React.Dispatch<React.SetStateAction<VendorModalType>>;
  setOpenStoplossModal?: React.Dispatch<React.SetStateAction<VendorModalType>>;
  setOpenClaimLineModal?: React.Dispatch<React.SetStateAction<ClaimsModalType>>;
  setOpenReimbursementModal?: React.Dispatch<
    React.SetStateAction<ReimbursementModalType>
  >;
  setOpenOtherClaimLineModal?: React.Dispatch<
    React.SetStateAction<OtherClaimsModalType>
  >;
  handleAddNewDocument?: () => void;
};

const Financials: React.FC<FinancialsType> = ({
  fixedCostColumns,
  variableCostColumns,
  fixedPrimary,
  fixedSecondary,
  fixedTertiary,
  fixedMember,
  variablePrimary,
  variableSecondary,
  variableMember,
  fixedCostTableLoading,
  variableCostTableLoading,
  setOpenVendorModal,
  setOpenStoplossModal,
  setOpenReimbursementModal,
  setOpenOtherClaimLineModal,
  handleAddNewDocument,
}) => {
  const { classes } = makeStyles()({
    contentContainer: {
      marginTop: '1rem',
      display: 'flex',
      flexDirection: 'column',
      gap: '1rem',
    },
    loaderContainer: {
      width: '100%',
      height: '300px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
  })();

  const makeAddButton = (label: string, onClick: () => void) => (
    <Button
      label={label}
      startIcon={<Add fill={patientAccessTheme.palette.primary[500]} />}
      variant="outlined"
      height="32px"
      width="auto"
      onClick={onClick}
    />
  );

  const messages = financialForensicsConstants;

  return (
    <Grid className={classes.contentContainer}>
      <CardComponent
        title={messages.financials.fixedTitle}
        body={
          fixedCostTableLoading ? (
            <CircularLoader />
          ) : (
            <ContributionTable
              columns={fixedCostColumns}
              planPrimaryData={fixedPrimary}
              planSecondaryData={fixedSecondary}
              memberData={fixedMember}
              planPrimaryAddButton={
                setOpenVendorModal &&
                setOpenStoplossModal &&
                handleAddNewDocument && (
                  <AddVendorDropdown
                    setOpenVendorModal={setOpenVendorModal}
                    setOpenStoplossModal={setOpenStoplossModal}
                    handleAddNewDocument={handleAddNewDocument}
                  />
                )
              }
              planSecondaryAddButton={makeAddButton('Add Vendor', () =>
                setOpenVendorModal?.({
                  open: true,
                  type: 'CREDITS',
                  actionType: 'ADD',
                }),
              )}
              memberAddButton={makeAddButton('Add Vendor', () =>
                setOpenVendorModal?.({
                  open: true,
                  type: 'MEMBER',
                  actionType: 'ADD',
                }),
              )}
              planTertiaryData={fixedTertiary}
              planTertiaryAddButton={makeAddButton('Add Stop Loss', () =>
                setOpenStoplossModal?.({
                  open: true,
                  type: 'STOPLOSS',
                  actionType: 'ADD',
                }),
              )}
              {...messages.contributionTable.fixedCosts}
            />
          )
        }
        topRightItem={
          <ContributionEquation
            {...calculateContributions(
              {
                planContribution: {
                  fees: fixedPrimary,
                  stoploss: [],
                  credits: fixedSecondary,
                },
                memberContribution: fixedMember,
              },
              fixedTertiary ?? [],
            )}
          />
        }
      />
      <CardComponent
        title={messages.financials.variableTitle}
        body={
          variableCostTableLoading ? (
            <Grid className={classes.loaderContainer}>
              <CircularLoader />
            </Grid>
          ) : (
            <ContributionTable
              columns={variableCostColumns}
              planPrimaryData={variablePrimary}
              planSecondaryData={variableSecondary}
              memberData={variableMember}
              planPrimaryAddButton={makeAddButton('Add Claim Line', () =>
                setOpenOtherClaimLineModal?.({
                  open: true,
                  type: 'FEES',
                  actionType: 'ADD',
                }),
              )}
              planSecondaryAddButton={makeAddButton('Add Recoveries', () =>
                setOpenReimbursementModal?.({
                  open: true,
                  type: 'REBATES',
                  actionType: 'ADD',
                }),
              )}
              memberAddButton={makeAddButton('Add Claim Line', () =>
                setOpenOtherClaimLineModal?.({
                  open: true,
                  type: 'MEMBER',
                  actionType: 'ADD',
                }),
              )}
              {...messages.contributionTable.variableCosts}
            />
          )
        }
        topRightItem={
          <ContributionEquation
            {...calculateVariableContributions({
              planContribution: {
                fees: variablePrimary,
                rebates: variableSecondary,
              },
              memberContribution: variableMember,
            })}
            isFixed={false}
          />
        }
      />
    </Grid>
  );
};

export default Financials;
